<template>
  <div class="cart__body">
    <div class="cart__head">
      <div class="cart__title-wrapper">
        <h5 class="cart__title">Cart</h5>
        <h5 v-if="totalCartItems > 0" :key="totalCartItems" class="cart__count" id="cart-title" aria-live="polite">({{ totalCartItems }} items)</h5>
      </div>
      <button v-if="!isCart" class="cart__close" aria-label="Close" @click="emit('on-toggle', false)">
        <svg class="cart__close-icon">
          <use href="#close"></use>
        </svg>
      </button>
    </div>
    <!-- <div v-if="!isEmpty" class="cart__freeShippingBar">
      <span v-if="freeLimitAchive != 100 && freeLimitCount" class="cart__discountCode">You’re ${{freeLimitCount}} away from getting free shipping.</span>
      <span v-if="cartProps.discountCodeMsg && freeLimitAchive === 100" class="cart__discountCode">{{cartProps.discountCodeMsg}}</span>
      <div v-if="cartProps.freeShippingLimit" class="cart__shippingBar-wrapper">
        <span class="cart__shippingBar-amount">$0</span>
        <div class="cart__shippingBar" :style="{ '--ship-progress': `${freeLimitAchive}%`}"></div>
        <span class="cart__shippingBar-amount">${{cartProps.freeShippingLimit}}</span>
      </div>
      <span v-if="cartProps.freeShippingMsg && freeLimitAchive === 100" class="cart__freeShippingMsg" v-html="cartProps.freeShippingMsg"></span>
    </div> -->
    
    <!-- Threshold Tracker -->
    <div v-if="!isEmpty && showThresholdTracker" class="cart__thresholdTracker">
      <div v-if="annualCapDetails.thresholdProgress < 100" class="cart__threshold-message">
        <span class="cart__threshold-message-copy">Progress toward your annual StioSphere pricing spend limits: </span>
        <span class="cart__threshold-message-amount">${{ annualCapDetails.totalWithCart.toFixed(2) }}</span>
      </div>
      <div v-if="annualCapDetails.thresholdProgress === 100" class="cart__threshold-message cart__threshold-message--complete">
        That’s all for this year’s StioSphere savings! Your pricing will reset on Jan. 1
      </div>
      <div v-if="annualCapDetails.annualThreshold" class="cart__thresholdBar-wrapper">
        <span class="cart__thresholdBar-amount">$0</span>
        <div class="cart__thresholdBar" :style="{ '--threshold-progress': `${annualCapDetails.thresholdProgress}%`}"></div>
        <span class="cart__thresholdBar-amount">${{ annualCapDetails.annualThreshold }}</span>
      </div>
    </div>
    
     <form v-if="!isEmpty" class="cart__form" action="/cart" method="post">
       <div v-if="isSeparating" class="cart-body__overlay">
         <div class="cart-body__loader cart-body--processing"></div>
       </div>
      <div v-if="cartConfirmationObj.itemRemoved" class="cartItem--removed">{{ cartConfirmationObj.message }}</div>
      <CartItem v-if="addingProduct" :placeholder="true" />
      <CartItem
        v-for="(item, index) in items"
        :cart="items"
        :key="item.variant_id + item.key"
        :item="item"
        :cartConfirmationObj="
          item.line === cartConfirmationObj.line && !addingProduct ? cartConfirmationObj : { visiblity: false }
        "
        @quantity-change="change"
      />
      <template v-if="shouldShowUpsellItem && Object.keys(upsellConfig).length">
        <CartUpsell :data="upsellConfig.item" />
      </template>
      <div v-if="showDailyKarmaWidget && items && items.length" class="dk-flat-static"></div>
      <div v-if="items && items.length" class="cart__bottom">
        <div v-if="subTotalFormatted" class="cart__calc">
          <span class="cart__calc-label">Subtotal</span>
          <span class="cart__calc-value">{{ subTotalFormatted }}</span>
        </div>

        <button
          :data-stio-gtm-id="isCart ? 'Cart: Checkout CTA' : 'Cart Drawer: Checkout CTA'"
          @click="checkingOut = true"
          name="checkout"
          class="btn btn--secondary btn--medium cart__checkout"
        >
          <span :class="{ 'cart__checkout-inner--processing': checkingOut }" class="cart__checkout-inner">
            CHECKOUT
          </span>
        </button>
        <CartExpressCheckout :isCartOpen="isCartOpen" />
        <div class="cart__highlight-text">{{ cartProps.cartHighLightMsg }}</div>
      </div>
    </form>
    <div v-else class="cart__empty">{{ cartProps.cartEmptyText }}</div>
  </div>
</template>

<script setup>
  import { ref, computed, onBeforeMount, watch, toRefs } from 'vue';
  import { useStore } from 'vuex';
  import cartHelpers from '../api';
  import CartItem from './cart-item.vue';
  import CartUpsell from './cart-upsell.vue';
  import CartExpressCheckout from './cart-express-checkout.vue';

  const { status } = cartHelpers;
  const store = useStore();
  var cbs = ref([]);
  var upsellConfig = ref({});
  var checkingOut = ref(false);
  const isSeparating = ref(false);

  const props = defineProps({
    isCart: {
      type: Boolean,
      default: false,
    },

    isCartOpen: {
      type: Boolean,
      default: false,
    },

    cartConfirmationObj: {
      type: Object,
      default: {
        visiblity: false,
        message: 'Item Added To Cart',
      },
    },

    showDailyKarmaWidget: {
      type: Boolean,
      default: false,
    },
  });

  onBeforeMount(async () => {
    // Setting window function for additem
    window.Additem = addItem;
    window.updateCartItems = update;
    window.changeThenAdd = changeThenAdd;
    window.cartItems = items.value;

    // Initialise store init function
    initCart();

    // upsell product function
    getUpsell();

    // daily karma refresh
    window.flatRefreshCartCallback = () => {
      initCart();
    };
  });

  const emit = defineEmits(['on-toggle', 'change-cartConfirmationObj']);

  const cart = computed(() => store.state.cart);
  const totalCount = computed(() => store.state.cart.item_count);
  const cartStatus = computed(() => store.state.status);
  const isEmpty = computed(() => store.state.status === status.empty);
  const inventory = computed(() => store.state.inventory);

  const cartProps = computed(() => {
    return window.cartProps;
  });

  const items = computed(() => {
    const items = cart.value.items || [];
    return items.map((item, index) => ({
      ...item,
      image: item.featured_image,
      line: index + 1,
    }));
  });

  const shouldShowUpsellItem = computed(() => {
    return (
      cartProps.value.upsellEnabled === true &&
      cart.value.items &&
      cart.value.items.length &&
      !cart.value.items.some(({ handle }) => handle === cartProps.value.upsellProductHandle)
    );
  });

  const totalCartItems = computed(() => {
    return totalCount.value;
  });

  const addingProduct = computed(() => {
    return cartStatus.value === status.adding;
  });

  const subTotal = computed(() => {
    return cart.value.total_price / 100;
  });

  const subTotalFormatted = computed(() => {
    return window.formattedPrice(cart.value.total_price / 100);
  });

  const freeLimitAchive = computed(() => {
    const completed = (subTotal.value / Number(cartProps.value.freeShippingLimit)) * 100;
    return Math.min(100, completed);
  });

  const freeLimitCount = computed(() => {
    const available = Number(cartProps.value.freeShippingLimit) - subTotal.value;
    const value = Math.max(0, available);
    return value % 1 != 0 ? value.toFixed(2) : value;
  });

  // Threshold tracker computed properties
  const annualCapDetails = computed(() => {
    const cartItemsSubTotal = cart.value?.items?.reduce((total, item) => {
      // Filter items based on the discount conditions
      const hasValidDiscount = item.discounts?.some(discount => {
        const validTags = ['EMP', 'PRO', 'FF'];
        return validTags.some(tag => discount.title.includes(tag)) && discount.amount > 0;
      });

      // If the item meets the conditions, add its final_price to the subtotal
      if (hasValidDiscount) {
        return total + ((parseFloat(item.final_price) / 100) * item.quantity);
      }

      return total;
    }, 0);

    return window.getAnnualCapDetails ? window.getAnnualCapDetails(cartItemsSubTotal) : {
      annualThreshold: 0,
      amountSpend: 0,
      thresholdProgress: 0,
      thresholdRemaining: 0,
      hasReachedThreshold: false,
      hasThreshold: false
    };
  });

  console.log('annualCapDetails - ', annualCapDetails);
  

  const isEMPPROFFUser = computed(() => {
    const discountTags = ['EMP', 'FF', 'PRO'];
    const hasDiscountTags = window.customerTags && window.customerTags.some(customerTag => 
      discountTags.some(discountTag => customerTag.includes(discountTag))
    );

    return hasDiscountTags;
  });

  const showThresholdTracker = computed(() => {
    return isEMPPROFFUser.value && annualCapDetails.value.hasThreshold;
  });

  const getUpsell = async () => {
    await fetch(`/products/${cartProps.value.upsellProductHandle}?view=json`)
      .then((resp) => resp.json())
      .then((data) => {
        upsellConfig.value.product = data;
        upsellConfig.value.variant = upsellConfig.value.product.variants.find(
          ({ id: variantId }) => Number(variantId) === Number(cartProps.value.upsellVariantId)
        );
        const { product, variant } = upsellConfig.value;
        upsellConfig.value.item = {
          message: cartProps.value.upsellMsg,
          image: variant.featured_image,
          title: product.title,
          variantTitle: variant.title,
          compareAtPrice: product.compare_at_price / 100,
          price: variant.price / 100,
          variantId: Number(cartProps.value.upsellVariantId),
          available: variant.available,
          handle: product.handle,
          product: product,
        };
      })
      .catch((error) => {
        console.error('Error', error);
      });
  };

  const initCart = () => {
    store.dispatch('init').then(() => {

      if (isEMPPROFFUser.value && isSeparating.value != true) {
        separateLineItems();
      }
    });
  };

  const getItemInventory = (variantId) => {
    return inventory.value.find((variant) => variant.variant_id === variantId) || {};
  };

  const getLatestItemIndex = () => {
    let currentLatestIndex = 0;

    (items.value || []).forEach((item) => {
      currentLatestIndex = Math.max(currentLatestIndex, item?.properties?._lineIndex || 0);
    });

    return currentLatestIndex;
  };

  const normalizePropsWithoutLineIndex = (props) => {
    // Make a shallow copy and remove _lineIndex
    const { _lineIndex, ...rest } = props || {};
    return rest;
  };

  const addItem = async (data, messageObj = { visiblity: true }) => {  
    // Normalize data to always be an array for consistent processing
    const itemsToProcess = Array.isArray(data) ? data : [data];
    const latestItemIndex = getLatestItemIndex();
    const itemsToAdd = [];
    let lineIndexCounter = 1;
    
    // Helper function to find existing item in cart
    const findExistingItem = (item) => {
      return (items.value || []).find(
        (cartItem) =>
          cartItem.variant_id === Number(item.id) &&
          JSON.stringify(normalizePropsWithoutLineIndex(cartItem.properties)) ===
            JSON.stringify(normalizePropsWithoutLineIndex(item.properties))
      );
    };
    
    // Process each item sequentially
    for (const item of itemsToProcess) {
      const existingItem = findExistingItem(item);
      
      if (existingItem) {
        // Item exists - update quantity (wait for completion)
        await change({
          line: existingItem.line,
          quantity: existingItem.quantity + (item.quantity || 1),
        }, existingItem, true, messageObj);
      } else {
        // Item doesn't exist - add to itemsToAdd array
        itemsToAdd.push({
          ...item,
          properties: {
            ...item.properties,
            _lineIndex: latestItemIndex + lineIndexCounter
          }
        });
        lineIndexCounter++;
      }
    }
    
    // Add all new items at once
    if (itemsToAdd.length > 0) {
      store.dispatch('add', itemsToAdd);
      emit('change-cartConfirmationObj', {
        visiblity: messageObj.visiblity,
        message: 'Item Added To Cart',
        line: 1,
      });
    }
  };

  const change = async(data, productData, hasIncreaseQty, messageObj = { visiblity: true }) => {
    const latestItemIndex = getLatestItemIndex();
    const lastAddedItem = items.value.find((item) => (item.line == data.line));
    const isLastAddedItemWithNoDiscount = lastAddedItem.discounted_price == lastAddedItem.price;

    const addPayload = {
      id: productData.variant_id,
      quantity: 1,
      properties: {
        ...data.properties,
        _lineIndex: latestItemIndex + 1
      }
    };

    // EMP,PRO,FF user => No Customer Discount and Partial Discount Case
    if (isEMPPROFFUser.value && hasIncreaseQty && !isLastAddedItemWithNoDiscount) {
      if (parseFloat(annualCapDetails.value.thresholdRemaining) == 0 || (parseFloat(annualCapDetails.value.thresholdRemaining || 0) < productData.discounted_price/100)) {
          if (parseFloat(annualCapDetails.value.thresholdRemaining) > 0) {
            await store.dispatch('add', addPayload);
            emit('change-cartConfirmationObj', {
              visiblity: messageObj.visiblity,
              message: 'Item Added To Cart',
              line: 1,
            });
          } else {
            const itemAddedToCart = (items.value || []).find(
              (item) =>
                item.variant_id === Number(productData.id) &&
                item.discounted_price == item.price
            );
            if (itemAddedToCart) {
              data = {
                line: itemAddedToCart.line,
                quantity: itemAddedToCart.quantity + 1,
              };
              await store.dispatch('change', data);
              emit('change-cartConfirmationObj', {
                visiblity: messageObj.visiblity,
                message: 'Item Updated',
                line: itemAddedToCart.line,
              });
            } else {
              await store.dispatch('add', addPayload);
              emit('change-cartConfirmationObj', {
                visiblity: messageObj.visiblity,
                message: 'Item Added To Cart',
                line: 1,
              });
            }
          }

          return;
      }
    }

    data = {
      ...data,
      properties: {
        ...data.properties,
        _lineIndex: productData?.properties?._lineIndex || latestItemIndex + 1
      }
    };
    
    const result = await store.dispatch('change', data);
    
    if(isEMPPROFFUser.value && !isSeparating.value) {
      separateLineItems();
    }
    
    emit('change-cartConfirmationObj', {
      visiblity: messageObj.visiblity,
      message: 'Item Updated',
      line: data.line,
    });

    if (data.quantity === 0) {
      emit('change-cartConfirmationObj', {
        message: 'Your item was removed!',
        itemRemoved: true,
      });
    }
    
    return result;
  };

  const update = (data) => {
    emit('change-cartConfirmationObj', {
      visiblity: false,
    });

    store.dispatch('update', data).then(() => {
      emit('change-cartConfirmationObj', {
        visiblity: true,
        message: 'Item Updated',
        line: 1,
      });
    });
  };

  const changeThenAdd = (data) => {
    emit('change-cartConfirmationObj', {
      visiblity: false,
    });

    store.dispatch('changeThenAdd', data).then(() => {
      if(isEMPPROFFUser.value && !isSeparating.value) {
        separateLineItems();
      }
      emit('change-cartConfirmationObj', {
        visiblity: true,
        message: 'Item Updated',
        line: 1,
      });
    });
  };

  const clear = () => {
    store.dispatch('clear');
  };

  const getTagDiscountedPrice = (price, compareAtPrice, percent) => {
    if (compareAtPrice > 0) {
      // Discount from compareAtPrice if present
      return compareAtPrice * (1 - percent);
    }
    return price * (1 - percent);
  };

  const separateLineItems = async (line = null) => {
    try {
      if (isSeparating.value) return;
      isSeparating.value = true;

      const cartItems = items.value || [];
      const sortedCartItems = [...cartItems].sort((a, b) => {
        const indexA = parseInt(a.properties?._lineIndex ?? 0, 10);
        const indexB = parseInt(b.properties?._lineIndex ?? 0, 10);
        
        // Sort by _lineIndex (oldest first - lowest _lineIndex first)
        if (a.properties?._lineIndex && b.properties?._lineIndex) {
          return indexA - indexB;
        }
        
        // If neither has _lineIndex, maintain original API order
        if (!a.properties?._lineIndex && !b.properties?._lineIndex) {
          return 0;
        }
        
        // If only one has _lineIndex, prioritize the one without it
        if (!a.properties?._lineIndex) return -1;
        if (!b.properties?._lineIndex) return 1;
        
        return indexA - indexB;
      });

      let remainingThreshold = annualCapDetails.value.annualThreshold - annualCapDetails.value.amountSpend;
      let itemsToModify = { line: line, change: [], add: [] };
      let modifiedCartLines = [];

      console.log(sortedCartItems,"separateLineItems sortedCartItems");

      sortedCartItems.forEach((lineItem, index) => {
        const cartLineInventory = getItemInventory(lineItem.variant_id);
        const productTags = cartLineInventory?.tags || lineItem.tags || [];
        const discountPercent = window.customerDiscount(productTags);

        if (discountPercent === 0) {
          return;
        }

        if (remainingThreshold <= 0) {
          return;
        }

        const originalPrice = lineItem.price / 100;
        const compareAtPrice = cartLineInventory?.compare_at_price / 100 || 0;
        const baseDiscountedPrice = getTagDiscountedPrice(originalPrice, compareAtPrice, discountPercent);
        const currentDiscountedPrice = lineItem.discounted_price / 100;

        if (!modifiedCartLines[index]) {
          modifiedCartLines[index] = [];
        }

        for (let i = 0; i < lineItem.quantity; i++) {
          const priceToCompare = compareAtPrice > 0 ? compareAtPrice : originalPrice;

          if (remainingThreshold >= baseDiscountedPrice) {
            if (baseDiscountedPrice < originalPrice) {
              modifiedCartLines[index].push({
                currentPrice: Number(currentDiscountedPrice.toFixed(2)),
                expectedPrice: Number(baseDiscountedPrice.toFixed(2)),
                quantityIndex: i + 1,
              });
              remainingThreshold -= baseDiscountedPrice;
            }
          } else if (remainingThreshold > 0) {
            const newPrice = priceToCompare - (parseFloat(remainingThreshold) * discountPercent);
            const partialDiscountVal = ((100 - (newPrice/priceToCompare * 100))/100);

            const partialDiscountedPrice = getTagDiscountedPrice(originalPrice, compareAtPrice, partialDiscountVal);

            if (partialDiscountedPrice < originalPrice) {
              modifiedCartLines[index].push({
                currentPrice: Number(currentDiscountedPrice.toFixed(2)),
                expectedPrice: Number(partialDiscountedPrice.toFixed(2)),
                quantityIndex: i + 1,
              });
              remainingThreshold = 0;
            }
          } else {
            modifiedCartLines[index].push({
              currentPrice: Number(currentDiscountedPrice.toFixed(2)),
              expectedPrice: Number(originalPrice.toFixed(2)),
              quantityIndex: i + 1,
            });
          }
        }
      });

      console.log("separateLineItems modifiedCartLines:", modifiedCartLines);

      modifiedCartLines.forEach((modifiedCartLine, itemIndex) => {
        if (!modifiedCartLine?.length) return;

        const lineItem = sortedCartItems[itemIndex];
        const latestItemIndex = getLatestItemIndex();
        let lineIndexCounter = 1;

        const priceGroups = modifiedCartLine.reduce((groups, modification) => {
          const key = `${modification.currentPrice}_${modification.expectedPrice}`;
          groups[key] = groups[key] || {
            currentPrice: modification.currentPrice,
            expectedPrice: modification.expectedPrice,
            count: 0
          };
          groups[key].count++;
          return groups;
        }, {});

         const priceGroupValues = Object.values(priceGroups);
         
         if (priceGroupValues.length === 1) {
           const [singleGroup] = priceGroupValues;
           const isPriceSame = Math.abs(singleGroup.currentPrice - singleGroup.expectedPrice) < 0.01;
           
           if (isPriceSame) {
             return;
           }
         }

         // Check if we have any groups with same prices
         const hasSamePriceGroups = priceGroupValues.some(group => 
           Math.abs(group.currentPrice - group.expectedPrice) < 0.01
         );

         // If no same price groups, we need to remove the original item
         if (!hasSamePriceGroups) {
           itemsToModify.change.push({
             line: lineItem.line,
             quantity: 0
           });
         }

         priceGroupValues.forEach(group => {
           const isPriceSame = Math.abs(group.currentPrice - group.expectedPrice) < 0.01;
           
           if (isPriceSame) {
             // Check if this group contains the last quantity indices (only if total qty > 2)
             const isLastQuantities = lineItem.quantity > 2 && 
               modifiedCartLine
                 .filter(mod => Math.abs(mod.currentPrice - mod.expectedPrice) < 0.01)
                 .every(mod => mod.quantityIndex > lineItem.quantity - group.count);
             
             if (isLastQuantities) {
               // Change original item to 0 quantity and add new item
               itemsToModify.change.push({
                 line: lineItem.line,
                 quantity: 0
               });
               itemsToModify.add.push({
                 id: lineItem.variant_id,
                 quantity: group.count,
                 properties: {
                   ...lineItem.properties,
                   _lineIndex: latestItemIndex + lineIndexCounter,
                 }
               });
               lineIndexCounter++;
             } else {
               // Update original item quantity
               itemsToModify.change.push({
                 line: lineItem.line,
                 quantity: group.count
               });
             }
           } else {
             // Prices are different - add as new item
             itemsToModify.add.push({
               id: lineItem.variant_id,
               quantity: group.count,
               properties: {
                 ...lineItem.properties,
                 _lineIndex: latestItemIndex + lineIndexCounter,
               }
             });
             lineIndexCounter++;
           }
         });
      });

      console.log("separateLineItems Final itemsToModify:", itemsToModify);

      if (itemsToModify.add.length > 0 || itemsToModify.change.length > 0) {
        await store.dispatch('changeThenAdd', itemsToModify);
      } else {
        console.log("No changes needed - cart is already properly configured");
      }
    } catch (error) {
      console.log('error while separating', error);
    } finally {
      isSeparating.value = false;
    }
  };

  watch(cartStatus, (value) => {
    cbs.value.forEach((cb, index) => {
      cb(value);
      cbs.value.splice(index, 1);
    });
  });

  watch(totalCount, (count) => {
    const cartEl = document.querySelectorAll('.js-cart-count');
    cartEl.forEach((ele) => {
      const el = ele;
      el.dataset.cart = count;
      el.setAttribute('aria-label', `Cart with ${count} items`)
    });
  });

  watch(items, () => {
    window.cartItems = items.value;
    window.dispatchEvent(new Event('cartItemsUpdated'));
  });
</script>
